/**
 * Sales Sync Section Component
 *
 * Displays sales sync status and provides actions for admin:
 * - View sync status (initialized, batch count, last batch details)
 * - Initialize sync (tare) with a start date
 * - Unblock stuck batches
 *
 * Shown in the POS Config tab of the BrandDrawer when a POS config exists.
 */

import { useState } from 'react';
import { Button, Badge, Modal, Input } from '@/components/ui';
import { useAdminSalesSyncStatus } from '../hooks/useAdminCompanies';
import { useInitSalesSync, useUnblockSalesSync } from '../hooks/useAdminMutations';

interface SalesSyncSectionProps {
  brandId: string;
}

/**
 * Format an ISO date string for display in fr-FR locale
 */
function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get badge variant based on batch status
 */
function getStatusVariant(status: string): 'success' | 'danger' | 'warning' | 'info' {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'danger';
    case 'PROCESSING':
    case 'PENDING':
      return 'warning';
    case 'PARTIAL':
      return 'info';
    default:
      return 'info';
  }
}

export function SalesSyncSection({ brandId }: SalesSyncSectionProps) {
  const { data: syncStatus, isLoading } = useAdminSalesSyncStatus(brandId);
  const initMutation = useInitSalesSync();
  const unblockMutation = useUnblockSalesSync();

  // Init modal state
  const [showInitModal, setShowInitModal] = useState(false);
  const [initStartDate, setInitStartDate] = useState('');

  const handleInit = async () => {
    if (!initStartDate) return;

    // Send just the date (YYYY-MM-DD). The backend resolves the brand's
    // cutoff hour + timezone and creates a COMPLETED tare batch with a
    // 1-minute window (e.g. 04:00 → 04:01). The scheduler then picks
    // up from endDate on the next automatic sync cycle.
    await initMutation.mutateAsync({ brandId, startDate: initStartDate });
    setShowInitModal(false);
    setInitStartDate('');
  };

  const handleUnblock = async () => {
    if (!syncStatus?.stuckBatchId) return;
    const confirmed = window.confirm(
      'Confirmer le deblocage du batch ? Il sera marque comme FAILED et la prochaine sync automatique repartira du dernier batch COMPLETED.'
    );
    if (!confirmed) return;
    await unblockMutation.mutateAsync(brandId);
  };

  if (isLoading) {
    return (
      <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200 animate-pulse">
        <div className="h-4 bg-neutral-200 rounded w-48 mb-3" />
        <div className="h-3 bg-neutral-200 rounded w-64 mb-2" />
        <div className="h-3 bg-neutral-200 rounded w-40" />
      </div>
    );
  }

  if (!syncStatus) return null;

  return (
    <>
      <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200 space-y-3">
        <h4 className="text-sm font-semibold text-neutral-800">Synchronisation Ventes</h4>

        {/* Status line */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-neutral-700">Statut :</span>
          {syncStatus.isInitialized ? (
            <>
              <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm text-neutral-600">
                Initialisee ({syncStatus.totalBatches} batch{syncStatus.totalBatches > 1 ? 'es' : ''})
              </span>
            </>
          ) : (
            <>
              <span className="inline-block w-2 h-2 rounded-full bg-neutral-400" />
              <span className="text-sm text-neutral-500">Non initialisee</span>
            </>
          )}
        </div>

        {/* Last batch details */}
        {syncStatus.lastBatch && (
          <div className="space-y-1 pl-4 border-l-2 border-neutral-200">
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-600">Dernier batch :</span>
              <span className="text-sm text-neutral-500">
                {formatDate(syncStatus.lastBatch.createdAt)}
              </span>
              <Badge variant={getStatusVariant(syncStatus.lastBatch.status)}>
                {syncStatus.lastBatch.status}
              </Badge>
            </div>
            {syncStatus.lastBatch.source !== 'SEED' && (
              <div className="text-sm text-neutral-500">
                {syncStatus.lastBatch.totalReceipts} ticket{syncStatus.lastBatch.totalReceipts !== 1 ? 's' : ''},{' '}
                {syncStatus.lastBatch.totalItems} article{syncStatus.lastBatch.totalItems !== 1 ? 's' : ''}
              </div>
            )}
            {syncStatus.lastBatch.errorMessage && (
              <div className="text-sm text-danger-600">
                Erreur : {syncStatus.lastBatch.errorMessage}
              </div>
            )}
          </div>
        )}

        {/* Stuck batch warning */}
        {syncStatus.hasStuckBatch && syncStatus.stuckBatchId && (
          <div className="flex items-center gap-2 p-2 bg-amber-50 rounded border border-amber-200">
            <span className="text-sm text-amber-700 font-medium">
              Batch bloque en PROCESSING (id: {syncStatus.stuckBatchId.substring(0, 8)}...)
            </span>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 pt-1">
          {!syncStatus.isInitialized && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => setShowInitModal(true)}
            >
              Initialiser
            </Button>
          )}

          {syncStatus.hasStuckBatch && (
            <Button
              size="sm"
              variant="danger"
              onClick={handleUnblock}
              isLoading={unblockMutation.isPending}
            >
              Debloquer le batch
            </Button>
          )}
        </div>
      </div>

      {/* Init Modal */}
      <Modal
        isOpen={showInitModal}
        onClose={() => setShowInitModal(false)}
        title="Initialiser la synchronisation"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Choisissez la date de depart (tare). La prochaine synchronisation automatique importera
            les tickets a partir de cette date.
          </p>
          <Input
            label="Date de depart"
            type="date"
            value={initStartDate}
            onChange={(e) => setInitStartDate(e.target.value)}
            required
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowInitModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleInit}
              isLoading={initMutation.isPending}
              disabled={!initStartDate}
            >
              Initialiser
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
